import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { verifyFlutterwave } from "@/api/services/flutterwaveService";
import { verifyPlanSubscription } from "@/api/services/plan";
import { getActiveSubscription } from "@/api/services/subscription";
import { storeService } from "@/api/services/storeService";

import apiClient from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";

type ParsedResult = {
  parsed: unknown | null;
  error: string | null;
  rawResp: string | null;
  verifyResponse?: unknown | null;
  verifyError?: string | null;
  subscribeResponse?: unknown | null;
  subscribeError?: string | null;
  orderResponse?: unknown | null;
  orderError?: string | null;
};

type ParsedData = { status?: string; data?: { id?: string; txRef?: string; currency?: string; status?: string } };

export default function WalletPaymentCallback() {
  const location = useLocation();
  const isDonatePath =
    location.pathname.includes("/app/donate/verify") ||
    location.pathname.includes("/donate/verify");
  const donateVerifiedRef = useRef(false);
  const [result, setResult] = useState<ParsedResult>({
    parsed: null,
    error: null,
    rawResp: null,
    verifyResponse: null,
    verifyError: null,
    subscribeResponse: null,
    subscribeError: null,
    orderResponse: null,
    orderError: null
  });
  const [isVerifying, setIsVerifying] = useState(false);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  useEffect(() => {
    const statusParam = searchParams.get("status");
    const txRefParam = searchParams.get("tx_ref");
    const transactionIdParam = searchParams.get("transaction_id");

    if (statusParam) localStorage.setItem("pendingVerifyStatus", statusParam);
    if (txRefParam) localStorage.setItem("pendingVerifyTxRef", txRefParam);
    if (transactionIdParam) localStorage.setItem("pendingVerifyTransactionId", transactionIdParam);

    const rawResp = searchParams.get("resp");
    if (!rawResp) {
      setResult({
        parsed: null,
        error: null,
        rawResp: null,
        verifyResponse: null,
        verifyError: null,
        subscribeResponse: null,
        subscribeError: null,
        orderResponse: null,
        orderError: null
      });
      console.log("Wallet payment callback: missing resp param");
      return;
    }

    try {
      const decoded = decodeURIComponent(rawResp);
      const parsed = JSON.parse(decoded);
      const localRedirectUrl = `${window.location.origin}/wallet/payment/callback`;

      if (parsed?.data?.redirectUrl) {
        parsed.data.redirectUrl = localRedirectUrl;
      }
      if (typeof parsed?.data?.chargeRequestData === "string") {
        try {
          const charge = JSON.parse(parsed.data.chargeRequestData);
          if (charge?.redirect_url) {
            charge.redirect_url = localRedirectUrl;
            parsed.data.chargeRequestData = JSON.stringify(charge);
          }
        } catch {
          // ignore chargeRequestData parse errors
        }
      }

      setResult({
        parsed,
        error: null,
        rawResp: decoded,
        verifyResponse: null,
        verifyError: null,
        orderResponse: null,
        orderError: null
      });
      console.log("Wallet payment callback parsed:", parsed);
    } catch (err: unknown) {
      setResult({
        parsed: null,
        error: (err as { message?: string, response?: { data?: { message?: string, error?: string, status?: string } } })?.message || "Failed to parse `resp`",
        rawResp,
        verifyResponse: null,
        verifyError: null,
        orderResponse: null,
        orderError: null
      });
      console.log("Wallet payment callback parse error:", err);
    }
  }, [searchParams]);

  useEffect(() => {
    const parsed = result.parsed as { status?: string; data?: { status?: string; txRef?: string; id?: string } } | null;
    const statusFromParsed = parsed?.status || parsed?.data?.status;
    const txRefFromParsed = parsed?.data?.txRef;
    const transactionIdFromParsed = parsed?.data?.id;

    const status =
      statusFromParsed ||
      localStorage.getItem("pendingVerifyStatus");
    const txRef =
      txRefFromParsed ||
      localStorage.getItem("pendingVerifyTxRef");
    const transactionId =
      transactionIdFromParsed ||
      localStorage.getItem("pendingVerifyTransactionId");

    if (!status || !txRef || !transactionId) return;

    setIsVerifying(true);

    if (isDonatePath) {
      apiClient
        .get(
          `${ENDPOINTS.DONATE.VERIFY_NGO_DONATION}?transaction_id=${encodeURIComponent(
            String(transactionId)
          )}&tx_ref=${encodeURIComponent(String(txRef))}`
        )
        .then((res) => {
          donateVerifiedRef.current = true;
          if (res.data?.success) {
            setResult((prev) => ({
              ...prev,
              verifyResponse: { success: true },
              verifyError: null,
              orderResponse: res,
              orderError: null
            }));
            localStorage.setItem("donateSuccess", "true");
            localStorage.removeItem("pendingCheckoutFlow");
            localStorage.removeItem("pendingDonateTxRef");
            localStorage.removeItem("pendingDonateId");
          } else {
            throw new Error(res.data?.message || "Donation verification failed");
          }
        })
        .catch((err: unknown) => {
          donateVerifiedRef.current = true;
          setResult((prev) => ({
            ...prev,
            verifyResponse: null,
            verifyError: (err as { message?: string, response?: { data?: { message?: string, error?: string, status?: string } } })?.response?.data?.message || (err as { message?: string, response?: { data?: { message?: string, error?: string, status?: string } } })?.message || "Donation verification failed",
            orderResponse: null,
            orderError: (err as { message?: string, response?: { data?: { message?: string, error?: string, status?: string } } })?.response?.data?.message || (err as { message?: string, response?: { data?: { message?: string, error?: string, status?: string } } })?.message || "Donation verification failed"
          }));
        })
        .finally(() => {
          setIsVerifying(false);
        });
      return;
    }

    verifyFlutterwave({
      status: String(status),
      tx_ref: String(txRef),
      transaction_id: String(transactionId)
    })
      .then((res) => {
        setResult((prev) => ({ ...prev, verifyResponse: res, verifyError: null }));
        console.log("Wallet payment verification:", res);
      })
      .catch((err: unknown) => {
        setResult((prev) => ({
          ...prev,
          verifyResponse: null,
          verifyError: (err as { message?: string, response?: { data?: { message?: string, error?: string, status?: string } } })?.message || "Verification failed"
        }));
        console.log("Wallet payment verification error:", err);
      })
      .finally(() => {
        setIsVerifying(false);
      });
  }, [result.parsed, isDonatePath]);

  const extractPlanId = (parsed: { data?: { meta?: unknown; chargeRequestData?: string } } | null): string | null => {
    if (!parsed) return null;

    const meta = parsed?.data?.meta;
    if (meta) {
      if (typeof meta === "object" && !Array.isArray(meta)) {
        const metaObj = meta as unknown as Record<string, unknown>;
        if (metaObj.plan_id) return String(metaObj.plan_id);
      }
      if (Array.isArray(meta)) {
        const found = meta.find(
          (m: Record<string, unknown>) =>
            m?.plan_id ||
            m?.planId ||
            m?.metaname === "plan_id" ||
            m?.name === "plan_id"
        );
        if (found?.plan_id) return String(found.plan_id);
        if (found?.planId) return String(found.planId);
        if (found?.metavalue) return String(found.metavalue);
        if (found?.value) return String(found.value);
      }
    }

    const chargeRequestData = parsed?.data?.chargeRequestData;
    if (typeof chargeRequestData === "string") {
      try {
        const parsedCharge = JSON.parse(chargeRequestData);
        return (
          extractPlanId({ data: { meta: parsedCharge?.meta } }) ||
          (parsedCharge?.plan_id ? String(parsedCharge.plan_id) : null)
        );
      } catch {
        return null;
      }
    }

    return null;
  };

  const extractHotspotId = (parsed: { data?: { meta?: unknown; chargeRequestData?: string } } | null): string | null => {
    if (!parsed) return null;

    const meta = parsed?.data?.meta;
    if (meta) {
      if (typeof meta === "object" && !Array.isArray(meta)) {
        const metaObj = meta as unknown as Record<string, unknown>;
        if (metaObj.hotspot_id) return String(metaObj.hotspot_id);
        if (metaObj.hotspotId) return String(metaObj.hotspotId);
      }
      if (Array.isArray(meta)) {
        const found = meta.find(
          (m: Record<string, unknown>) =>
            m?.hotspot_id ||
            m?.hotspotId ||
            m?.metaname === "hotspot_id" ||
            m?.name === "hotspot_id"
        );
        if (found?.hotspot_id) return String(found.hotspot_id);
        if (found?.hotspotId) return String(found.hotspotId);
        if (found?.metavalue) return String(found.metavalue);
        if (found?.value) return String(found.value);
      }
    }

    const chargeRequestData = parsed?.data?.chargeRequestData;
    if (typeof chargeRequestData === "string") {
      try {
        const parsedCharge = JSON.parse(chargeRequestData);
        return (
          extractHotspotId({ data: { meta: parsedCharge?.meta } }) ||
          (parsedCharge?.hotspot_id ? String(parsedCharge.hotspot_id) : null) ||
          (parsedCharge?.hotspotId ? String(parsedCharge.hotspotId) : null)
        );
      } catch {
        return null;
      }
    }

    return null;
  };

  const extractOrderType = (parsed: { data?: { meta?: unknown; chargeRequestData?: string } } | null): string | null => {
    if (!parsed) return null;

    const meta = parsed?.data?.meta;
    if (meta) {
      if (typeof meta === "object" && !Array.isArray(meta)) {
        const metaObj = meta as unknown as Record<string, unknown>;
        if (metaObj.order_type) return String(metaObj.order_type);
        if (metaObj.orderType) return String(metaObj.orderType);
      }
      if (Array.isArray(meta)) {
        const found = meta.find(
          (m: Record<string, unknown>) =>
            m?.order_type ||
            m?.orderType ||
            m?.metaname === "order_type" ||
            m?.name === "order_type"
        );
        if (found?.order_type) return String(found.order_type);
        if (found?.orderType) return String(found.orderType);
        if (found?.metavalue) return String(found.metavalue);
        if (found?.value) return String(found.value);
      }
    }

    const chargeRequestData = parsed?.data?.chargeRequestData;
    if (typeof chargeRequestData === "string") {
      try {
        const parsedCharge = JSON.parse(chargeRequestData);
        return (
          extractOrderType({ data: { meta: parsedCharge?.meta } }) ||
          (parsedCharge?.order_type ? String(parsedCharge.order_type) : null) ||
          (parsedCharge?.orderType ? String(parsedCharge.orderType) : null)
        );
      } catch {
        return null;
      }
    }

    return null;
  };

  const extractTopupAmount = (parsed: { data?: { meta?: unknown; chargeRequestData?: string } } | null): number | null => {
    if (!parsed) return null;
    const meta = parsed?.data?.meta;
    if (meta) {
      if (typeof meta === "object" && !Array.isArray(meta)) {
        const metaObj = meta as unknown as Record<string, unknown>;
        if (metaObj.topup_amount) return Number(metaObj.topup_amount);
        if (metaObj.amount) return Number(metaObj.amount);
      }
      if (Array.isArray(meta)) {
        const found = meta.find(
          (m: Record<string, unknown>) =>
            m?.topup_amount ||
            m?.amount ||
            m?.metaname === "topup_amount" ||
            m?.name === "topup_amount"
        );
        if (found?.topup_amount) return Number(found.topup_amount);
        if (found?.amount) return Number(found.amount);
        if (found?.metavalue) return Number(found.metavalue);
        if (found?.value) return Number(found.value);
      }
    }

    const chargeRequestData = parsed?.data?.chargeRequestData;
    if (typeof chargeRequestData === "string") {
      try {
        const parsedCharge = JSON.parse(chargeRequestData);
        return (
          extractTopupAmount({ data: { meta: parsedCharge?.meta } }) ||
          (parsedCharge?.topup_amount ? Number(parsedCharge.topup_amount) : null) ||
          (parsedCharge?.amount ? Number(parsedCharge.amount) : null)
        );
      } catch {
        return null;
      }
    }

    return null;
  };

  useEffect(() => {
    if (isDonatePath && donateVerifiedRef.current) return;
    const verify = result.verifyResponse as { success?: boolean; status?: string } | null;
    const isVerified =
      verify?.success === true || verify?.status === "success";

    if (!isVerified) return;

    const localhostCallbackUrl = localStorage.getItem("pendingLocalhostCallbackUrl");
    const shouldOpenLocalhost =
      localStorage.getItem("pendingLocalhostCallback") === "true" &&
      localhostCallbackUrl &&
      !localStorage.getItem("pendingLocalhostCallbackOpened");

    if (shouldOpenLocalhost) {
      const popup = window.open(localhostCallbackUrl, "_blank", "noopener,noreferrer");
      if (popup) {
        localStorage.setItem("pendingLocalhostCallbackOpened", "true");
      }
    }

    const orderType =
      extractOrderType(result.parsed) ||
      (localStorage.getItem("pendingWalletTopup") ? "wallet_topup" : null) ||
      localStorage.getItem("pendingCheckoutFlow") ||
      (isDonatePath ? "donate" : null);
    const transactionId =
      (result.parsed as ParsedData)?.data?.id ||
      localStorage.getItem("pendingVerifyTransactionId");
    const txRef =
      (result.parsed as ParsedData)?.data?.txRef ||
      localStorage.getItem("pendingVerifyTxRef");

    if (orderType === "wallet_topup") {
      const amount =
        extractTopupAmount(result.parsed) ||
        Number(localStorage.getItem("pendingWalletTopupAmount"));
      const currency =
        (result.parsed as ParsedData)?.data?.currency ||
        localStorage.getItem("pendingWalletTopupCurrency") ||
        "USD";
      if (!amount) {
        setResult((prev) => ({
          ...prev,
          orderResponse: null,
          orderError: "Missing top-up amount for wallet funding."
        }));
        return;
      }

      setResult((prev) => ({
        ...prev,
        orderResponse: {
          success: true,
          amount: Number(amount),
          currency: String(currency),
          paymentMethod: "card",
          paymentMethodId: String(txRef || transactionId || "")
        },
        orderError: null
      }));
      localStorage.setItem("walletTopupSuccess", "true");
      localStorage.removeItem("pendingWalletTopup");
      localStorage.removeItem("pendingWalletTopupAmount");
      localStorage.removeItem("pendingWalletTopupCurrency");
      return;
    }

    if (orderType === "store") {
      if (!transactionId || !txRef) {
        setResult((prev) => ({
          ...prev,
          orderResponse: null,
          orderError: "Missing transaction reference for checkout."
        }));
        return;
      }

      storeService
        .checkout({ paymentMethod: "card", transactionId: String(transactionId), txRef: String(txRef) })
        .then((res) => {
          setResult((prev) => ({ ...prev, orderResponse: res, orderError: null }));
          localStorage.setItem("storeOrderSuccess", "true");
          if ((res as unknown as Record<string, unknown>)?._id) {
            localStorage.setItem("storeOrderId", String((res as { _id: unknown })._id));
          }
          localStorage.removeItem("pendingCheckoutFlow");
        })
        .catch((err: unknown) => {
          setResult((prev) => ({
            ...prev,
            orderResponse: null,
            orderError: (err as { message?: string, response?: { data?: { message?: string, error?: string, status?: string } } })?.message || "Checkout failed"
          }));
        });
      return;
    }

    if (orderType === "donate") {
      if (!transactionId || !txRef) {
        setResult((prev) => ({
          ...prev,
          orderResponse: null,
          orderError: "Missing transaction reference for donation."
        }));
        return;
      }

      import("@/api/client").then(({ default: apiClient }) => {
        import("@/api/endpoints").then(({ ENDPOINTS }) => {
          apiClient.get(`${ENDPOINTS.DONATE.VERIFY_NGO_DONATION}?transaction_id=${transactionId}&tx_ref=${txRef}`)
            .then((res) => {
              if (res.data?.success) {
                setResult((prev) => ({ ...prev, orderResponse: res, orderError: null }));
                localStorage.setItem("donateSuccess", "true");
                localStorage.removeItem("pendingCheckoutFlow");
                localStorage.removeItem("pendingDonateTxRef");
                localStorage.removeItem("pendingDonateId");
              } else {
                throw new Error(res.data?.message || "Donation verification failed");
              }
            })
            .catch((err: unknown) => {
              setResult((prev) => ({
                ...prev,
                orderResponse: null,
                orderError: (err as { message?: string, response?: { data?: { message?: string, error?: string, status?: string } } })?.response?.data?.message || (err as { message?: string, response?: { data?: { message?: string, error?: string, status?: string } } })?.message || "Donation verification failed"
              }));
            });
        });
      });
      return;
    }

    // Use the plan-specific verify endpoint: api/plans/subscribe/verify
    const status =
      (result.parsed as ParsedData)?.status ||
      (result.parsed as ParsedData)?.data?.status ||
      localStorage.getItem("pendingVerifyStatus") ||
      "successful";

    if (!txRef || !transactionId) {
      setResult((prev) => ({
        ...prev,
        subscribeResponse: null,
        subscribeError: "Missing tx_ref or transaction_id for plan verification."
      }));
      return;
    }

    verifyPlanSubscription({
      status: String(status),
      tx_ref: String(txRef),
      transaction_id: String(transactionId)
    })
      .then((res) => {
        setResult((prev) => ({ ...prev, subscribeResponse: res, subscribeError: null }));
        console.log("Plan subscription verified:", res);
        localStorage.setItem("planSubscribeSuccess", "true");
        localStorage.removeItem("pendingPlanId");
        localStorage.removeItem("pendingPlanName");
        localStorage.removeItem("pendingPlanPeriod");
        localStorage.removeItem("pendingHotspotId");
        localStorage.removeItem("pendingVerifyStatus");
        localStorage.removeItem("pendingVerifyTxRef");
        localStorage.removeItem("pendingVerifyTransactionId");
      })
      .catch((err: unknown) => {
        setResult((prev) => ({
          ...prev,
          subscribeResponse: null,
          subscribeError: (err as { message?: string, response?: { data?: { message?: string, error?: string, status?: string } } })?.message || "Plan verification failed"
        }));
        console.log("Plan verification error:", err);
      });
  }, [result.verifyResponse, result.parsed]);

  const orderSuccess = Boolean(result.orderResponse) && !result.orderError;
  const orderError = result.orderError;

  const verifyStatus =
    (result.verifyResponse as unknown as Record<string, unknown>)?.success === true ||
    (result.verifyResponse as unknown as Record<string, unknown>)?.status === "success"
      ? "success"
      : result.verifyError
      ? "error"
      : null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white dark:bg-[#2a2b2e] rounded-2xl shadow p-6 border border-gray-200 dark:border-gray-800 text-center">
        {isVerifying ? (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              Verifying Payment
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              Please wait while we confirm your transaction.
            </p>
          </>
        ) : verifyStatus === "success" && orderError ? (
          <>
            <div className="w-10 h-10 rounded-full bg-yellow-100 text-yellow-700 flex items-center justify-center mx-auto mb-3">
              !
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              Payment Verified, Order Pending
            </h1>
            <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-2">
              {orderError}
            </p>
          </>
        ) : verifyStatus === "success" && result.subscribeError ? (
          <>
            <div className="w-10 h-10 rounded-full bg-yellow-100 text-yellow-700 flex items-center justify-center mx-auto mb-3">
              !
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              Payment Verified, Subscription Pending
            </h1>
            <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-2">
              {result.subscribeError}
            </p>
          </>
        ) : verifyStatus === "success" && orderSuccess ? (
          <>
            <div className="w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-3">
              âœ“
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              Order Successful
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              Your payment was verified and your order has been placed.
            </p>
          </>
        ) : verifyStatus === "success" ? (
          <>
            <div className="w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-3">
              ✓
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              Payment Successful
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              Your payment was verified successfully.
            </p>
          </>
        ) : verifyStatus === "error" ? (
          <>
            <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-3">
              ✕
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              Payment Failed
            </h1>
            <p className="text-sm text-red-600 dark:text-red-400 mt-2">
              {result.verifyError || "Verification failed."}
            </p>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              Preparing Verification
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              Reading payment response...
            </p>
          </>
        )}
      </div>
    </div>
  );
}
